<template>
  <div class="payment-reminders-editor">
    <div v-if="!isEdit" class="payment-reminders-editor__transaction">
      <div class="payment-reminders-editor__label">交易方式</div>
      <el-select
        style="width: 100%;"
        size="mini"
        :disabled="disabled"
        filterable
        clearable
        :value="customerTransactionId"
        @change="handleTransactionChange"
      >
        <el-option
          v-for="item in customerTransactions"
          :key="item.id"
          :label="item.name"
          :value="item.id"
        />
      </el-select>
    </div>

    <div v-if="displayPaymentReminders.length" class="payment-reminders-editor__list">
      <div v-if="isEdit" class="payment-reminders-editor__actions">
        <el-button
          size="mini"
          type="text"
          class="payment-reminders-editor__fill-button"
          @click="handleFillExpectedAmounts"
        >
          填充比例金额
        </el-button>
      </div>
      <div
        v-for="reminder in displayPaymentReminders"
        :key="`payment-reminder-${reminder.stage_index}`"
        class="payment-reminders-editor__item"
      >
        <div class="payment-reminders-editor__meta">
          <span>阶段{{ reminder.stage_index }}</span>
          <span>{{ reminder.percentage }}%</span>
          <span v-if="!isEdit">金额 {{ formatAmount(reminder.amount) }}</span>
        </div>
        <div
          v-if="isEdit && reminder.amountMismatch"
          class="payment-reminders-editor__warning"
        >
          当前金额与按比例计算结果不一致，按比例应为 {{ formatAmount(reminder.expectedAmount) }}
        </div>
        <el-input
          v-if="isEdit"
          size="mini"
          type="number"
          :value="reminder.amount"
          @input="handleReminderAmountChange(reminder.stage_index, $event)"
        >
          <template #prepend>金额</template>
        </el-input>
        <div
          v-if="isEdit && hasServerAmountChanged(reminder)"
          class="payment-reminders-editor__server-value"
        >
          原值: {{ formatAmount(getServerAmount(reminder)) }}
        </div>
        <el-date-picker
          style="width: 100%;"
          size="mini"
          type="date"
          value-format="yyyy-MM-dd"
          :disabled="disabled"
          :value="reminder.date"
          @input="handleReminderDateChange(reminder.stage_index, $event)"
        />
      </div>
    </div>

    <div v-else class="payment-reminders-editor__placeholder">
      {{ isEdit ? '暂无付款提醒' : '请先选择交易方式' }}
    </div>
  </div>
</template>

<script>
import { Vue, Component, Prop } from 'vue-property-decorator';
import Decimal from 'decimal.js';
import { allocateReminderAmounts } from '../utils/payment-reminders';

@Component
export default class FormItemPaymentReminders extends Vue {
  @Prop({ type: Array, default: () => [] }) value;
  @Prop({ type: Array, default: () => [] }) originalValue;
  @Prop({ type: [String, Number], default: '' }) customerTransactionId;
  @Prop({ type: Array, default: () => [] }) customerTransactions;
  @Prop({ type: Object, default: null }) customerTransaction;
  @Prop({ type: [String, Number], default: 0 }) salesAmount;
  @Prop({ type: Boolean, default: false }) isEdit;
  @Prop({ type: Boolean, default: false }) disabled;

  get sortedValue() {
    return [...this.value].sort((a, b) => Number(a.stage_index) - Number(b.stage_index));
  }

  get originalReminderMap() {
    return this.originalValue.reduce((result, reminder) => {
      result[Number(reminder.stage_index)] = reminder;
      return result;
    }, {});
  }

  get displayPaymentReminders() {
    if (this.isEdit) {
      return this.attachReminderExpectations(this.sortedValue);
    }

    const stages = this.customerTransaction && Array.isArray(this.customerTransaction.stages)
      ? this.customerTransaction.stages
      : [];

    if (!stages.length) {
      return [];
    }

    const existingReminderMap = this.sortedValue.reduce((result, reminder) => {
      result[Number(reminder.stage_index)] = reminder;
      return result;
    }, {});

    const amounts = allocateReminderAmounts(this.salesAmount, stages);

    return stages.map((percentage, index) => {
      const stageIndex = index + 1;
      const existingReminder = existingReminderMap[stageIndex] || {};

      return {
        ...existingReminder,
        stage_index: stageIndex,
        percentage,
        amount: amounts[index],
        date: existingReminder.date || '',
      };
    });
  }

  attachReminderExpectations(reminders) {
    if (!reminders.length) {
      return [];
    }

    const expectedAmounts = allocateReminderAmounts(this.salesAmount, reminders.map(item => item.percentage));

    return reminders.map((reminder, index) => {
      const expectedAmount = expectedAmounts[index];
      const actualAmount = new Decimal(reminder.amount || 0);

      return {
        ...reminder,
        expectedAmount,
        amountMismatch: !actualAmount.equals(new Decimal(expectedAmount)),
      };
    });
  }

  formatAmount(amount) {
    return new Decimal(amount || 0).toFixed(2);
  }

  getServerAmount(reminder) {
    const serverReminder = this.originalReminderMap[Number(reminder.stage_index)];
    if (!serverReminder) {
      return null;
    }
    return serverReminder.amount;
  }

  hasServerAmountChanged(reminder) {
    const serverAmount = this.getServerAmount(reminder);
    if (serverAmount === null) {
      return false;
    }

    return !new Decimal(reminder.amount || 0).equals(new Decimal(serverAmount || 0));
  }

  updateReminder(stageIndex, updater) {
    const reminders = this.displayPaymentReminders.map((reminder) => {
      if (Number(reminder.stage_index) !== Number(stageIndex)) {
        return reminder;
      }

      return updater(reminder);
    });

    this.$emit('change-payment-reminders', reminders);
  }

  handleTransactionChange(value) {
    this.$emit('change-customer-transaction-id', value);
  }

  handleReminderAmountChange(stageIndex, amount) {
    this.updateReminder(stageIndex, reminder => ({
      ...reminder,
      amount,
    }));
  }

  handleReminderDateChange(stageIndex, date) {
    this.updateReminder(stageIndex, reminder => ({
        ...reminder,
        date,
      }));
  }

  handleFillExpectedAmounts() {
    const reminders = this.displayPaymentReminders.map(reminder => ({
      ...reminder,
      amount: reminder.expectedAmount || reminder.amount,
    }));

    this.$emit('change-payment-reminders', reminders);
  }
}
</script>

<style scoped>
.payment-reminders-editor {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.payment-reminders-editor__transaction,
.payment-reminders-editor__item {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.payment-reminders-editor__label,
.payment-reminders-editor__meta {
  font-size: 12px;
  color: #606266;
}

.payment-reminders-editor__meta {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.payment-reminders-editor__list {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.payment-reminders-editor__actions {
  display: flex;
  justify-content: flex-start;
}

.payment-reminders-editor__fill-button {
  padding: 0;
  font-size: 12px;
}

.payment-reminders-editor__placeholder {
  font-size: 12px;
  color: #909399;
}

.payment-reminders-editor__warning {
  font-size: 12px;
  color: #e6a23c;
}

.payment-reminders-editor__server-value {
  font-size: 12px;
  color: #e6a23c;
  font-weight: 600;
  margin-top: -2px;
}
</style>
